import { EditNote } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { Fragment } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import TextEditorInput from "../../../../../components/form/TextEditorInput";
import { STATUSES } from "../../../../../enums/AppointmentStatus";
import usePut from "../../../../../hooks/usePut";
import TemplateAutoComplete from "../../../form/TemplateAutoComplete";
import { TREATMENT_UPDATING } from "../../../../../enums/ApiPaths";
import { APPOINTMENT_GENERAL, TREATMENT } from "../../../../../enums/QueryKeys";
import useUser from "../../../../../hooks/useUser";
import { GlobalDataContext } from "../../../../../contexts/GlobalData";
import { VIEW_TEMPLATES } from "../../../../../enums/PermissionTitles";
import { TemplateCategory } from "../../../../../enums/TemplateCategory";
import ActionRow from "../../../../admin/clinic/components/actions/ActionRow";

export function canEditTreatment(appointment, roles) {
  return (
    !roles.user &&
    String(appointment?.status).toUpperCase() == STATUSES.FINISHED
  );
}

export default function TreatmentEditing({ initValue }) {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = React.useState(false);
  const initTreatment = initValue?.treatment;
  const queryKey = [TREATMENT + initValue?.id];
  const url = TREATMENT_UPDATING;
  const { roles } = useUser();
  const update = usePut(url, queryKey, [APPOINTMENT_GENERAL + initValue?.id]);
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const isViewTemplatePermissionAllowed = isPermissionAllowed(VIEW_TEMPLATES);

  const methods = useForm({
    defaultValues: {
      appointmentId: initValue?.id,
      description: initTreatment?.description,
      treatmentId: initTreatment?.id ?? null,
    },
  });

  React.useEffect(() => {
    methods.setValue("appointmentId", initValue?.id);
    methods.setValue("description", initTreatment?.description);
    methods.setValue("treatmentId", initTreatment?.id);
    methods.setValue("template", initTreatment?.template);
  }, [initValue]);

  function handleSetValue(val) {
    methods.setValue("description", val.description);
  }

  if (!canEditTreatment(initValue, roles)) {
    return null;
  }

  async function submit(e) {
    const payload = {
      ...e,
    };

    delete payload.template;

    const res = await update.submitCallback(payload);
    if (res) {
      handleDialog();
    }
  }

  function handleDialog() {
    setOpenDialog((prev) => !prev);
  }

  return (
    <Fragment>
      <Divider />
      <ActionRow
        justButton
        icon={<EditNote />}
        label={t("EditDoctorsLetter")}
        description={t("EditDoctorsLetterDesc")}
        onClick={() => handleDialog()}
        testId={"appointment.details.treatment-editing-button"}
      />

      <FormProvider {...methods}>
        <Tooltip title={t("EditDoctorsLetter")}>
          <Dialog
            open={openDialog}
            onClose={() => handleDialog()}
            maxWidth={"lg"}
            fullWidth
            data-testid={"appointment.details.treatment-editing-dialog"}
          >
            <DialogContent sx={{ width: "100%" }}>
              <Box sx={{ width: "100%" }}>
                <form onSubmit={methods.handleSubmit(submit)}>
                  <Stack mb={2}>
                    <Typography variant={"h5"} sx={{ mb: 2 }}>
                      {t("EditDoctorsLetter")}
                    </Typography>
                    {isViewTemplatePermissionAllowed && (
                      <TemplateAutoComplete
                        control={methods.control}
                        handleSetValue={(val) => handleSetValue(val)}
                        category={TemplateCategory.THERAPY}
                      />
                    )}
                  </Stack>
                  <Controller
                    name={"description"}
                    render={({ field }) => (
                      <TextEditorInput
                        onChange={field.onChange}
                        {...field}
                        height={"450px"}
                        testId={"appointment.details.treatment-editing.description-input"}
                      />
                    )}
                  />
                  <Stack
                    mt={2}
                    justifyContent={"end"}
                    alignItems={"center"}
                    direction={"row"}
                    spacing={2}
                  >
                    <Box>
                      <Button
                        variant="contained"
                        type="submit"
                        data-testid={"appointment.details.treatment-editing.save-button"}
                      >
                        {t("Save")}
                      </Button>
                    </Box>
                    <Box>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleDialog()}
                        data-testid={"appointment.details.treatment-editing.cancel-button"}
                      >
                        {t("Cancel")}
                      </Button>
                    </Box>
                  </Stack>
                </form>
              </Box>
            </DialogContent>
          </Dialog>
        </Tooltip>
      </FormProvider>
    </Fragment>
  );
}
